import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import Combobox from "../Combobox.jsx";
import { CURRENCIES } from "../../lib/constants";
import { fmtMoney, periodLabel, inputCls } from "../../lib/salary";

export function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} bg-white rounded-2xl border border-slate-200 shadow-xl p-6 max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testid}-close`}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function F({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
      {children}
    </div>
  );
}

export default function PaymentDialog({ row, period, onClose, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: row.balance > 0 ? String(row.balance) : "",
    currency: row.currency,
    company: "",
    account: "",
    reference: "",
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([api.get("/masters/company"), api.get("/masters/account")]).then(([c, a]) => {
      setCompanies(c.data);
      setAccounts(a.data);
    });
  }, []);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be greater than 0");
    if (!form.company) return toast.error("Paid From Company is required");
    if (!form.account) return toast.error("Paid From Account is required");
    setSaving(true);
    try {
      await api.post("/salary/payments", {
        employee_id: row.employee_id, period, kind: row.kind,
        ...form, amount: Number(form.amount),
      });
      toast.success(row.private ? "Payment recorded (private ledger)" : "Payment recorded & posted to register");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Record payment — ${row.employee_name}`} onClose={onClose} testid="salary-payment-dialog">
      <div className="text-sm text-slate-600 mb-4">
        {row.kind === "quarterly" ? "Quarterly Payment / Incentive" : "Salary"} for <span className="font-semibold text-slate-900">{periodLabel(period)}</span>
        {" · "}Balance <span className="font-semibold tabular">{row.currency} {fmtMoney(row.balance)}</span>
      </div>
      <div className={`text-xs rounded-lg px-3 py-2 mb-4 ${row.private ? "bg-slate-100 text-slate-600" : "bg-blue-50 text-blue-800"}`} data-testid="salary-payment-visibility-note">
        {row.private
          ? "China Office — this payment stays in the private salary ledger only. Nothing is written to the shared transaction register."
          : "Other — this payment will also be posted as Money Paid → Salary in the transaction register (visible to accounting)."}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Payment Date" required>
          <input type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} className={inputCls} data-testid="salary-payment-date" />
        </F>
        <F label="Amount" required>
          <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} className={`${inputCls} text-right tabular`} data-testid="salary-payment-amount" />
        </F>
        <F label="Currency" required>
          <Combobox value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES.map((c) => ({ value: c, label: c }))} testid="salary-payment-currency" allowClear={false} />
        </F>
        <F label="Paid From Company" required>
          <Combobox value={form.company} onChange={(v) => set("company", v)} options={companies.map((c) => ({ value: c.name, label: c.name }))} testid="salary-payment-company" placeholder="Company…" />
        </F>
        <div className="col-span-2">
          <F label="Paid From Account" required>
            <Combobox value={form.account} onChange={(v) => set("account", v)} options={accounts.map((a) => ({ value: a.name, label: `${a.name}${a.currency ? ` (${a.currency})` : ""}` }))} testid="salary-payment-account" placeholder="Cash / Bank / Wallet account…" />
          </F>
        </div>
        <F label="Reference (optional)">
          <input value={form.reference} onChange={(e) => set("reference", e.target.value)} className={inputCls} data-testid="salary-payment-reference" />
        </F>
        <F label="Notes (optional)">
          <input value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} data-testid="salary-payment-notes" />
        </F>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="salary-payment-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="h-11 px-5 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold disabled:opacity-60" data-testid="salary-payment-save">
          {saving ? "Saving…" : "Save payment"}
        </button>
      </div>
    </Modal>
  );
}
